// BUILD_VERSION: MODULAR-4000-STRICT-V403
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';
import { auth } from './firebaseConfig';
import LoginScreen from './components/LoginScreen';
import ModularWorkspace from './components/ModularWorkspace';
import DictaphoneMini from './components/DictaphoneMini';
import Modular1Screen from './components/Modular1Screen';
import Modular2Screen from './components/Modular2Screen';
import DictaphoneScreen from './components/DictaphoneScreen';
import ChatScreen from './components/ChatScreen';
import MessagingScreen from './components/MessagingScreen';
import ProfileScreen from './components/ProfileScreen';
import PruebaScreen from './components/PruebaScreen';
import PHDAssistanceScreen from './components/PHDAssistanceScreen';
import ManagementScreen from './components/ManagementScreen'; 
import DashboardUserScreen from './components/DashboardUserScreen';
import ErrorBoundary from './components/ErrorBoundary';
import { ViewState, SavedTranscription, ReportTemplate, UserProfile, Phrase, RegionalRestriction } from './types';
import { Lock, Mail, X, Globe } from 'lucide-react';
import { getRegionalAccess } from './utils/regionalAccess';

// Identificador único para esta pestaña/ventana para evitar eco en BroadcastChannel
if (typeof window !== 'undefined' && !(window as any).__MODULAR_TAB_ID__) {
  (window as any).__MODULAR_TAB_ID__ = Math.random().toString(36).substring(2, 15);
}
const TAB_ID = (window as any).__MODULAR_TAB_ID__;

import { 
  subscribeToReports, 
  subscribeToPhrases,
  savePhraseToCloud, 
  subscribeToUserProfile,
  subscribeToTranscriptions,
  saveTranscriptionToCloud,
  deleteTranscriptionFromCloud,
  clearTranscriptionsFromCloud,
  updateUserPresence,
  getUIState,
  saveUIState,
  saveUserProfile,
  subscribeToWorkGroups,
  subscribeToActiveChats,
  subscribeToRegionalRestrictions,
  subscribeToSystemPricing
} from './services/db';

const checkProfileIncomplete = (profile: UserProfile | null) => {
  if (!profile) return true;
  const essential = (profile.displayName || '').trim() !== '' &&
                    (profile.dni || '').trim() !== '' &&
                    (profile.college || '').trim() !== '' &&
                    (profile.collegeNumber || '').trim() !== '' &&
                    (profile.workplace || '').trim() !== '' &&
                    (profile.workCity || '').trim() !== '';

  if (profile.role === 'RESIDENT') return !essential;
  return !essential || !profile.mainSpecialty || !profile.specialties || profile.specialties.length < 1;
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncingCloud, setSyncingCloud] = useState(false);
  const [isProfileChecking, setIsProfileChecking] = useState(true);
  
  // --- INICIALIZACIÓN DE VISTA AISLADA ---
  const [currentView, setCurrentView] = useState<ViewState>(() => {
     const params = new URLSearchParams(window.location.search);
     const viewParam = params.get('view');
     if (viewParam && Object.values(ViewState).includes(viewParam as ViewState)) {
        return viewParam as ViewState;
     }
     const lastView = localStorage.getItem('modular_last_view');
     if (lastView && Object.values(ViewState).includes(lastView as ViewState)) {
        return lastView as ViewState;
     }
     return ViewState.DASHBOARD_USER;
  });

  const [showMiniDictaphone, setShowMiniDictaphone] = useState(false);
  const [isUserVerified, setIsUserVerified] = useState(() => {
    return localStorage.getItem('master_session_active') === 'true';
  });
  const [isProfileIncomplete, setIsProfileIncomplete] = useState(() => {
    if (localStorage.getItem('master_session_active') === 'true') return false;
    return false; // Default to false until checked
  });
  const [isForcedProfileUpdate, setIsForcedProfileUpdate] = useState(false);
  const [isRecorderWidget, setIsRecorderWidget] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [regionalRestrictions, setRegionalRestrictions] = useState<RegionalRestriction[]>([]);

  useEffect(() => {
    const unsubRestrictions = subscribeToRegionalRestrictions(setRegionalRestrictions);
    return () => {
      unsubRestrictions();
    };
  }, []);

  const isCurrentlyAdmin = useMemo(() => {
    const userEmail = (user?.email || auth.currentUser?.email || '').toLowerCase().trim();
    const isMaster = userEmail === 'dr.francisco.reyes@gmail.com' || user?.uid === 'guest_dev_offline' || auth.currentUser?.uid === 'guest_dev_offline';
    return isMaster || userProfile?.role === 'ADMIN';
  }, [user, userProfile]);

  const disabledRegionalModules = useMemo(() => {
    return getRegionalAccess(userProfile, regionalRestrictions, (isCurrentlyAdmin || !!userProfile?.internalTestingBypass)).disabledModules;
  }, [userProfile, regionalRestrictions, isCurrentlyAdmin]);

  const handleLogout = useCallback(async () => {
    if (user) localStorage.removeItem(`modular_verified_${user.uid}`);
    localStorage.removeItem('master_session_active');
    setIsUserVerified(false);
    setIsProfileChecking(true);
    setIsForcedProfileUpdate(false);
    if (user?.uid === 'guest_dev_offline') { setUser(null); return; }
    await signOut(auth);
  }, [user]);

  const handleGuestLogin = async () => {
     const offlineUser: any = { uid: 'guest_dev_offline', displayName: 'Desarrollador (Offline)', email: 'dev@modular.local', isAnonymous: true };
     setUser(offlineUser);
     setIsUserVerified(true);
     setIsProfileChecking(false);
     localStorage.setItem(`modular_verified_guest_dev_offline`, 'true');
  };

  const [selectedRad1ReportId, setSelectedRad1ReportId] = useState<string | null>(null);
  const [modular1EditReportId, setModular1EditReportId] = useState<string | null>(null);
  const [rad1InitialText, setRad1InitialText] = useState<string>('');

  // --- MOTOR DE PERSISTENCIA INDIVIDUAL v7.0 ---
  const [isPinned, setIsPinned] = useState(false);
  const hasInitializedPinRef = useRef(false);
  const resetLockRef = useRef(false);
  const [windowPersistenceEnabled, setWindowPersistenceEnabled] = useState(() => {
    return localStorage.getItem('modular_window_persistence') !== 'false';
  });
  const isPopOutRef = useRef(new URLSearchParams(window.location.search).get('layout') === 'single');

  useEffect(() => {
    const bc = new BroadcastChannel('modular_sync_channel');
    const params = new URLSearchParams(window.location.search);
    const isPopOut = params.get('layout') === 'single';
    
    let instanceId = params.get('instanceId');
    if (isPopOut && !instanceId) {
        instanceId = `WIN_${currentView}_${Date.now()}`;
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('instanceId', instanceId);
        window.history.replaceState(null, '', newUrl.toString());
    }
    
    if (isPopOut) {
        window.name = instanceId || `MODULAR_WIN_${currentView}`;
        // Recuperar estado inicial de anclaje (Solo una vez)
        if (!hasInitializedPinRef.current) {
            const registry = JSON.parse(localStorage.getItem('modular_static_positions') || '{}');
            if (registry[window.name]?.isPinned) {
                setIsPinned(true);
            }
            hasInitializedPinRef.current = true;
        }
    } else {
        window.name = 'MODULAR_MASTER';
    }

    const reportCurrentPosition = () => {
      if (!isPopOut) return;

      const winData = {
        view: currentView,
        instanceId: window.name,
        x: Math.round(window.screenLeft ?? window.screenX),
        y: Math.round(window.screenTop ?? window.screenY),
        w: Math.round(window.outerWidth),
        h: Math.round(window.outerHeight),
        sw: window.screen.width,
        sh: window.screen.height,
        isPinned: isPinned,
        lastUpdated: Date.now()
      };

      bc.postMessage({ type: 'HEARTBEAT_POS', data: winData, senderId: TAB_ID });
    };

    bc.onmessage = (event) => {
      // Ignorar mensajes que provienen de esta misma pestaña
      if (event.data.senderId === TAB_ID) return;

      if (window.name === 'MODULAR_MASTER' && event.data.type === 'HEARTBEAT_POS') {
        if (resetLockRef.current) return; // Ignorar si estamos en medio de un reset
        const { data } = event.data;
        
        // Solo guardar si la persistencia maestra está activa O si la ventana está anclada individualmente
        if (windowPersistenceEnabled || data.isPinned) {
            const staticRegistryRaw = localStorage.getItem('modular_static_positions');
            let staticRegistry: Record<string, any> = staticRegistryRaw ? JSON.parse(staticRegistryRaw) : {};
            
            staticRegistry[data.instanceId] = data;
            localStorage.setItem('modular_static_positions', JSON.stringify(staticRegistry));
        }
      }

      if (event.data.type === 'RESET_PERSISTENCE') {
        setIsPinned(false);
        if (window.name === 'MODULAR_MASTER') {
            resetLockRef.current = true;
            setTimeout(() => { resetLockRef.current = false; }, 2000);
        }
      }

      if (isPopOut && (event.data.type === 'FORCE_WINDOW_SAVE' || event.data.type === 'REQUEST_ALL_POSITIONS')) {
        reportCurrentPosition();
      }

      if (event.data.type === 'SYNC_LIBRARY_SETTINGS') {
        localStorage.setItem(event.data.key, event.data.value);
        window.dispatchEvent(new Event('app_settings_changed'));
      }

      if (event.data.type === 'THEME_CHANGED') {
        localStorage.setItem('app_theme', event.data.theme);
        window.dispatchEvent(new Event('app_theme_changed'));
      }
      
      if (event.data.type === 'RAD1_MIRROR_TEXT') setRad1InitialText(event.data.text);
      
      // Escuchar selección remota de RAD IA 1
      if (event.data.type === 'RAD1_REMOTE_SELECT') {
        setSelectedRad1ReportId(event.data.reportId);
      }
    };

    if (isPopOut) {
      window.addEventListener('resize', reportCurrentPosition);
      window.addEventListener('move', reportCurrentPosition);
      window.addEventListener('beforeunload', reportCurrentPosition);
      const interval = setInterval(reportCurrentPosition, 3000); 
      reportCurrentPosition();
      return () => {
        clearInterval(interval);
        window.removeEventListener('resize', reportCurrentPosition);
        window.removeEventListener('move', reportCurrentPosition);
        window.removeEventListener('beforeunload', reportCurrentPosition);
      };
    } else {
        localStorage.setItem('modular_last_view', currentView);
    }

    return () => bc.close();
  }, [currentView, isPinned, isPopOutRef, windowPersistenceEnabled]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('widget') === 'recorder') {
       setIsRecorderWidget(true);
    }
  }, []);
  
  const [activeNotification, setActiveNotification] = useState<{ sender: string, text: string, groupId?: string, type: 'group' | 'p2p' } | null>(null);
  const startAppTimeRef = useRef(Date.now() - 2000); // 2 second safety buffer
  const notificationTimerRef = useRef<any>(null);

  const currentViewRef = useRef(currentView);
  const profileRef = useRef(userProfile);

  useEffect(() => { currentViewRef.current = currentView; }, [currentView]);
  useEffect(() => { profileRef.current = userProfile; }, [userProfile]);

  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToSystemPricing(() => {});
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user || user.uid === 'guest_dev_offline') return;
    
    const showNotification = (sender: string, text: string, type: 'group' | 'p2p', id?: string) => {
      setActiveNotification({ sender, text, groupId: id, type });
      
      if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
      notificationTimerRef.current = setTimeout(() => {
        setActiveNotification(null);
      }, 5000);

      // Mobile vibration if available
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }
    };

    // 1. Group Messages Listener
    const unsubGroups = subscribeToWorkGroups(user.uid, (groups) => {
      const notificationsEnabled = localStorage.getItem('modular_enable_notifications') !== 'false';
      if (!notificationsEnabled) return;

      groups.forEach(group => {
        if (group.lastMessage && group.lastMessage.timestamp > startAppTimeRef.current) {
          if (group.lastMessage.senderId !== user.uid) {
            // Check specific group settings if available via ref
            const isMuted = profileRef.current?.notificationSettings?.mutedGroups?.includes(group.id);
            const groupToasts = profileRef.current?.notificationSettings?.groupToasts !== false;
            
            if (!isMuted && groupToasts && currentViewRef.current !== ViewState.COMMUNITY) {
              showNotification(
                `GRUPO: ${group.name}`, 
                `${group.lastMessage.senderName}: ${group.lastMessage.text}`, 
                'group', 
                group.id
              );
            }
          }
        }
      });
    });

    // 2. P2P Messages Listener
    const unsubChats = subscribeToActiveChats(user.uid, (chats) => {
      const notificationsEnabled = localStorage.getItem('modular_enable_notifications') !== 'false';
      if (!notificationsEnabled) return;

      chats.forEach(chat => {
        if (chat.lastMessage && chat.lastMessage.timestamp > startAppTimeRef.current) {
          if (chat.lastMessage.senderId !== user.uid) {
            const communityToasts = profileRef.current?.notificationSettings?.communityToasts !== false;
            
            if (communityToasts && currentViewRef.current !== ViewState.COMMUNITY) {
              showNotification(
                chat.otherUserName || 'Mensaje Privado', 
                chat.lastMessage.text, 
                'p2p'
              );
            }
          }
        }
      });
    });
    
    return () => {
      unsubGroups();
      unsubChats();
      if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
    };
  }, [user]); // Now only depends on user ID

  const handleNotificationClick = () => {
    setActiveNotification(null);
    setCurrentView(ViewState.COMMUNITY);
  };
  const [reports, setReports] = useState<ReportTemplate[]>([]);
  const [phrases, setPhrases] = useState<Phrase[]>([]);
  const [savedTranscriptions, setSavedTranscriptions] = useState<SavedTranscription[]>([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setSyncingCloud(true);
        try {
          const cloudState = await getUIState(currentUser.uid);
          if (cloudState && cloudState.settings) {
            const localUpdatedRaw = localStorage.getItem('workspace_settings_updated');
            const localUpdated = localUpdatedRaw ? parseInt(localUpdatedRaw, 10) : 0;
            const cloudUpdated = cloudState.lastUpdated || 0;

            if (localUpdated > cloudUpdated) {
              console.log("[Sync] Local settings are newer than cloud settings. Pushing local settings to cloud.");
              const keysToSync = [
                'workspace_layout', 'workspace_dimensions_v2', 'workspace_configurations',
                'workspace_sidebar_sections', 'workspace_sidebar_sections_libre', 'workspace_mobile_views_libre', 'app_profile', 'app_theme',
                'app_shortcut_space_record', 'app_shortcut_agent_double_a', 'modular_copy_rich_text', 'modular_window_persistence',
                'modular_auto_clear_reports', 'modular_auto_clear_phrases',
                'modular_live_registry', 'modular_static_positions', 'modular_last_view', 'dictaphone_mic_pos',
                'dictaphone_autoCopy', 'dictaphone_autoStop', 'dictaphone_silence', 'dictaphone_panel_proportions'
              ];
              const settings: Record<string, string> = {};
              keysToSync.forEach(key => {
                const val = localStorage.getItem(key);
                if (val !== null) settings[key] = val;
              });
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('module_theme_')) {
                  const val = localStorage.getItem(key);
                  if (val !== null) settings[key] = val;
                }
              }
              const uiState = { lastUpdated: localUpdated, settings };
              await saveUIState(currentUser.uid, uiState);
            } else {
              console.log("[Sync] Cloud settings are newer or equal. Applying cloud settings to local storage.");
              Object.entries(cloudState.settings).forEach(([key, value]) => {
                if (localStorage.getItem(key) !== value) {
                  localStorage.setItem(key, value);
                }
              });
              localStorage.setItem('workspace_settings_updated', String(cloudUpdated));
            }

            window.dispatchEvent(new Event('modular_interface_ready'));
            window.dispatchEvent(new Event('app_theme_changed'));
            window.dispatchEvent(new Event('app_shortcuts_changed'));
            window.dispatchEvent(new Event('app_settings_changed'));
            window.dispatchEvent(new Event('dictaphone_settings_update'));
          }
        } catch (e) {
          console.warn("Fallo sincronización de preferencias:", e);
        } finally {
          setSyncingCloud(false);
        }
        const userEmail = (currentUser.email || '').toLowerCase();
        const isMaster = userEmail === 'dr.francisco.reyes@gmail.com' || currentUser.uid === 'guest_dev_offline';
        if (isMaster) {
          localStorage.setItem('master_session_active', 'true');
          setIsUserVerified(true);
          setIsProfileIncomplete(false);
        } else {
          localStorage.removeItem('master_session_active');
        }

        const wasVerified = localStorage.getItem(`modular_verified_${currentUser.uid}`) === 'true';
        if (wasVerified) setIsUserVerified(true);
      } else {
        setUser(null);
        setIsUserVerified(false);
        setIsProfileChecking(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    updateUserPresence(user.uid);
    const interval = setInterval(() => updateUserPresence(user.uid), 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (user) {
        const unsubReports = subscribeToReports(user.uid, setReports);
        const unsubPhrases = subscribeToPhrases(user.uid, setPhrases);
        const unsubTranscriptions = subscribeToTranscriptions(user.uid, setSavedTranscriptions);
        if (user.uid === 'guest_dev_offline') {
           setIsUserVerified(true);
           setIsProfileChecking(false);
           return () => { unsubReports(); unsubPhrases(); unsubTranscriptions(); };
        }
        const unsubProfile = subscribeToUserProfile(user.uid, (profile) => {
           setUserProfile(profile);
           // BLOQUEO: Si el usuario está bloqueado, forzar logout o mostrar pantalla de bloqueo
           if (profile && profile.isBlocked) {
              handleLogout();
              alert("Su cuenta ha sido bloqueada por un administrador.");
              return;
           }

           // BYPASS MAESTRO: El Dr. Francisco Reyes siempre está verificado
           const userEmail = (user.email || '').toLowerCase();
           const isMaster = userEmail === 'dr.francisco.reyes@gmail.com' || user.uid === 'guest_dev_offline';
           
           if (isMaster) {
              localStorage.setItem('master_session_active', 'true');
           } else {
              localStorage.removeItem('master_session_active');
           }
           const isMasterSession = isMaster;

           const verified = isMasterSession || !!(profile && profile.isVerified);
           
           setIsUserVerified(verified);
           
           // El Master nunca tiene el perfil incompleto a ojos del sistema de bloqueo
           const isIncomplete = isMasterSession ? false : checkProfileIncomplete(profile);
           const hasBypass = !!(profile && profile.bypassProfileLock);
           setIsProfileIncomplete(isIncomplete && !hasBypass);
           
           localStorage.setItem(`modular_verified_${user.uid}`, verified ? 'true' : 'false');
           setIsProfileChecking(false);
        });
        const timeout = setTimeout(() => {
          setIsProfileChecking((current) => {
            if (current) {
              console.warn("Global profile check timeout. Proceeding...");
              return false;
            }
            return current;
          });
        }, 10000); // 10 seconds for robustness
        return () => { unsubReports(); unsubPhrases(); unsubTranscriptions(); unsubProfile(); clearTimeout(timeout); };
    }
  }, [user, handleLogout]);

  useEffect(() => {
    if (user && !isProfileChecking) {
      const userEmail = (user.email || '').toLowerCase();
      const isMasterEmail = userEmail === 'dr.francisco.reyes@gmail.com' || user.uid === 'guest_dev_offline';
      const isMasterSession = isMasterEmail;

      if (isMasterSession) {
        // Asegurar que el estado es correcto para el Maestro
        if (!isUserVerified) setIsUserVerified(true);
        if (isProfileIncomplete) setIsProfileIncomplete(false);
        if (localStorage.getItem('master_session_active') !== 'true') localStorage.setItem('master_session_active', 'true');
        return;
      }

      const isMobile = window.innerWidth < 1024;
      if (!isUserVerified) {
        // Opción B: Desactivar redirección intrusiva en móviles
        if (isMobile) return;

        // Si no está validado, forzar Perfil si no está ya ahí o en Dashboard
        if (currentView !== ViewState.PROFILE && currentView !== ViewState.DASHBOARD_USER) {
           setCurrentView(ViewState.PROFILE);
        }
      }
    }
  }, [user, isProfileChecking, isUserVerified, isProfileIncomplete, currentView]);

  const handleNavigate = (view: ViewState) => {
    const isMaster = (user?.email?.toLowerCase() || '') === 'dr.francisco.reyes@gmail.com' || user?.uid === 'guest_dev_offline';
    if (isMaster) {
      setCurrentView(view);
      return;
    }

    if (!isUserVerified && view !== ViewState.PROFILE && view !== ViewState.DASHBOARD_USER) {
      setCurrentView(ViewState.DASHBOARD_USER);
      return;
    }
    setCurrentView(view);
  };
  
  const handleSelectReportForRad1 = (report: ReportTemplate) => { 
    setSelectedRad1ReportId(report.id); 
    setCurrentView(ViewState.MODULAR_1); 
    // Emitir señal de sincronización inmediata
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.postMessage({ type: 'RAD1_REMOTE_SELECT', reportId: report.id, senderId: TAB_ID });
    bc.close();
  };

  const handleEditReportFromModular1 = (reportId: string) => {
    setModular1EditReportId(reportId);
    setCurrentView(ViewState.REPORTS);
  };

  const handleSendToEditor = (text: string) => {
    setCurrentView(ViewState.MODULAR_1);
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.postMessage({ type: 'RAD1_REPLACE', text, senderId: TAB_ID });
    bc.close();
    setRad1InitialText(text);
  };

  const handleSaveTranscription = async (item: SavedTranscription) => {
      const uid = user?.uid || 'guest_dev_offline';
      await saveTranscriptionToCloud(uid, item);
  };
  
  const handleDeleteTranscription = async (id: string) => {
      const uid = user?.uid || 'guest_dev_offline';
      await deleteTranscriptionFromCloud(uid, id);
  };

  const handleClearTranscriptions = async () => {
      const uid = user?.uid || 'guest_dev_offline';
      await clearTranscriptionsFromCloud(uid);
  };

  const handleSavePhrase = async (text: string) => {
      if (!user) return;
      const newPhrase = { id: Date.now().toString(), text: text, filterIds: [], isFavorite: false, isFrequent: false, usageCount: 0, createdAt: Date.now() };
      await savePhraseToCloud(user.uid, newPhrase);
  };

  if (isRecorderWidget) {
     return <DictaphoneMini isVisible={true} onClose={() => window.close()} onSave={handleSaveTranscription} isStandalone={true} />;
  }

  const renderModule = (view: ViewState) => {
    // RESTRICCIÓN DE SEGURIDAD: Usuarios no validados solo ven Perfil, Dashboard y Comunidad (restringida)
    if (!isUserVerified && view !== ViewState.PROFILE && view !== ViewState.DASHBOARD_USER && view !== ViewState.COMMUNITY) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-10 text-center bg-white">
          <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center text-amber-500 mb-6 animate-pulse">
            <Lock size={40} />
          </div>
          <h2 className="text-2xl font-black text-slate-800 mb-3 uppercase tracking-tight">Módulo en Revisión</h2>
          <p className="text-slate-500 max-w-sm leading-relaxed">
            Tu identidad profesional está siendo validada por la IA de MODULAR. 
            <br/><br/>
            Podrás acceder a este módulo una vez que el sistema confirme tu perfil de radiólogo o residente.
          </p>
          <button 
            onClick={() => setCurrentView(ViewState.PROFILE)}
            className="mt-8 px-6 py-2 bg-slate-800 text-white rounded-xl font-bold text-sm hover:bg-slate-700 transition-all"
          >
            Revisar mi Perfil
          </button>
        </div>
      );
    }

    // RESTRICCIÓN DE SEGURIDAD REGIONAL: Si el módulo está deshabilitado por política geográfica y el usuario no está exento
    const isExemptFromRegional = isCurrentlyAdmin || !!userProfile?.internalTestingBypass;
    const isModuleDisabled = !isExemptFromRegional && (
      disabledRegionalModules.includes(view) ||
      (disabledRegionalModules.includes('REPORTS') && [
        ViewState.REPORTS,
        ViewState.CREATE_REPORT,
        ViewState.EDIT_REPORT,
        ViewState.MANAGE_FILTERS,
        ViewState.RECYCLE_BIN
      ].includes(view)) ||
      (disabledRegionalModules.includes('PHRASES') && [
        ViewState.PHRASES,
        ViewState.CREATE_PHRASE,
        ViewState.EDIT_PHRASE
      ].includes(view))
    );

    if (isModuleDisabled) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-10 text-center bg-white">
          <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 mb-6">
            <Globe size={40} />
          </div>
          <h2 className="text-2xl font-black text-slate-800 mb-3 uppercase tracking-tight">Módulo no disponible en tu región</h2>
          <p className="text-slate-500 max-w-sm leading-relaxed">
            Este módulo no está disponible en tu región geográfica según las políticas activas de gobernanza clínica y regulatoria.
            <br/><br/>
            Si consideras que se trata de un error, contacta al administrador del sistema.
          </p>
          <button 
            onClick={() => handleNavigate(ViewState.DASHBOARD_USER)}
            className="mt-8 px-6 py-2 bg-slate-800 text-white rounded-xl font-bold text-sm hover:bg-slate-700 transition-all"
          >
            Ir al Inicio
          </button>
        </div>
      );
    }

    switch (view) {
      case ViewState.REPORTS:
      case ViewState.PHRASES:
      case ViewState.CREATE_REPORT:
      case ViewState.EDIT_REPORT:
      case ViewState.CREATE_PHRASE:
      case ViewState.EDIT_PHRASE:
      case ViewState.MANAGE_FILTERS:
      case ViewState.RECYCLE_BIN:
          return <PruebaScreen user={user} view={view} onNavigate={handleNavigate} onSelectForModular1={handleSelectReportForRad1} externalEditReportId={modular1EditReportId} onExternalEditConsumed={() => setModular1EditReportId(null)} />;
      case ViewState.MODULAR_1:
          return <Modular1Screen reports={reports} onSavePhrase={handleSavePhrase} initialReportId={selectedRad1ReportId} initialText={rad1InitialText} onEditReport={handleEditReportFromModular1} />;
      case ViewState.MODULAR_2:
          return <Modular2Screen reports={reports} />;
      case ViewState.DICTAPHONE:
          return <DictaphoneScreen savedTranscriptions={savedTranscriptions} onSaveTranscription={handleSaveTranscription} onDeleteTranscription={handleDeleteTranscription} onClearAll={handleClearTranscriptions} onSendToEditor={handleSendToEditor} onToggleMini={() => setShowMiniDictaphone(true)} onSavePhrase={handleSavePhrase} />;
      case ViewState.CHAT_IA:
          return <ChatScreen user={user} onNavigate={handleNavigate} />;
      case ViewState.COMMUNITY:
          return <MessagingScreen user={user} reports={reports} isVerified={isUserVerified} />;
      case ViewState.PROFILE:
          return <ProfileScreen user={user} initialProfile={userProfile} isLocked={isProfileIncomplete} onNavigate={handleNavigate} />;
      case ViewState.DASHBOARD_USER:
          return <DashboardUserScreen isVerified={isUserVerified} isProfileIncomplete={isProfileIncomplete} onNavigate={handleNavigate} />;
      case ViewState.PHD_ASSISTANCE:
          return <PHDAssistanceScreen reports={reports} />;
      case ViewState.ADMIN_PANEL: {
          const userEmail = (user?.email || '').toLowerCase();
          const isMaster = userEmail === 'dr.francisco.reyes@gmail.com' || user?.uid === 'guest_dev_offline';
          const isAdmin = isMaster || userProfile?.role === 'ADMIN';
          if (!isAdmin) {
            return (
              <div className="flex flex-col items-center justify-center p-10 text-center bg-white h-full relative z-10">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center text-red-500 mb-6">
                  <Lock size={32} />
                </div>
                <h2 className="text-xl font-black text-slate-800 mb-2 uppercase tracking-tight">Acceso Denegado</h2>
                <p className="text-sm text-slate-500 max-w-sm leading-relaxed mb-6">
                  Este módulo de Gestión Master está reservado exclusivamente para el Administrador del sistema.
                </p>
                <button 
                  onClick={() => handleNavigate(ViewState.DASHBOARD_USER)}
                  className="px-6 py-2 bg-slate-800 text-white rounded-xl font-bold text-xs hover:bg-slate-705 transition-all select-none"
                >
                  Volver al Dashboard
                </button>
              </div>
            );
          }
          return <ManagementScreen />;
      }
      default:
          return <div className="p-10 text-center text-gray-400">Módulo no encontrado: {view}</div>;
    }
  };

  const handleTogglePersistence = () => {
    const next = !windowPersistenceEnabled;
    setWindowPersistenceEnabled(next);
    localStorage.setItem('modular_window_persistence', String(next));
    if (next) {
        const bc = new BroadcastChannel('modular_sync_channel');
        bc.postMessage({ type: 'FORCE_WINDOW_SAVE' });
        setTimeout(() => bc.close(), 100);
    }
  };

  if (loading || (user && isProfileChecking) || syncingCloud) {
     return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#1e2024] text-white font-sans">
           <div className="relative w-20 h-20 mb-8 flex items-center justify-center">
              <div className="absolute inset-0 rounded-2xl bg-blue-600 animate-pulse opacity-20"></div>
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
           </div>
           <div className="flex flex-col items-center space-y-2">
              <h2 className="text-sm font-black uppercase tracking-[0.4em] text-blue-500">MODULAR</h2>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 animate-pulse">
                {syncingCloud ? 'Rescatando Memoria de Interfaz...' : 'Sincronizando Sistema...'}
              </span>
           </div>
        </div>
     );
  }

  if (!user) return <LoginScreen onGuestLogin={handleGuestLogin} />;

  return (
    <ErrorBoundary>
        <ModularWorkspace 
            activeView={currentView}
            renderModule={renderModule}
            onLogout={handleLogout}
            user={user}
            onNavigate={handleNavigate}
            isProfileIncomplete={isProfileIncomplete}
            reports={reports}
            isPopOut={isPopOutRef.current}
            isPinned={isPinned}
            onTogglePin={() => setIsPinned(!isPinned)}
            windowPersistenceEnabled={windowPersistenceEnabled}
            onTogglePersistence={handleTogglePersistence}
        />
        <DictaphoneMini isVisible={showMiniDictaphone} onClose={() => setShowMiniDictaphone(false)} onSave={handleSaveTranscription} isStandalone={false} />
        
        {/* Burbuja de Notificación Global */}
        {activeNotification && (
          <div 
            className={`fixed left-1/2 -translate-x-1/2 z-[1000] animate-in fade-in slide-in-from-top-4 duration-300 ${window.innerWidth < 1024 ? 'bottom-24' : 'top-6'}`}
          >
            <div 
              onClick={handleNotificationClick}
              className={`flex items-center gap-4 p-4 rounded-2xl shadow-2xl border cursor-pointer min-w-[300px] max-w-[90vw] transition-all active:scale-95 ${localStorage.getItem('app_theme') === 'dark' ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-blue-100'}`}
            >
              <div className="bg-blue-600 p-2.5 rounded-xl shadow-md shrink-0">
                <Mail size={18} className="text-white" />
              </div>
              <div className="flex-1 min-w-0 pr-2">
                <p className={`text-[10px] font-black uppercase tracking-widest ${localStorage.getItem('app_theme') === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                  {activeNotification.sender}
                </p>
                <p className={`text-sm font-bold truncate ${localStorage.getItem('app_theme') === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                  {activeNotification.text}
                </p>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); setActiveNotification(null); }}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
              >
                 <X size={16} />
              </button>
            </div>
          </div>
        )}
    </ErrorBoundary>
  );
}

export default App;